<template>
<div>
  <v-layout row wrap justify-center>
    <v-flex class="text-xs-center pt-2" xs12 md11>
      <div class="headline text-xs-center"><b>{{loading ? 'Loading' : taskData.name}}</b></div>
      <v-divider class="my-3"></v-divider>
    </v-flex>
  </v-layout>
  <v-layout v-if="loading" row justify-center>
    <v-flex class="text-xs-center py-5 my-5" xs12>
      <div class="py-5 my-5">
        <v-progress-circular color="accent" indeterminate></v-progress-circular>
      </div>
    </v-flex>
  </v-layout>
  <v-layout v-else-if="status == 'START'" row wrap justify-center>
    <v-flex class="pt-1 text-xs-center" xs12>
      <div class="subheading text-xs-center">
        <div class="pb-2">Questions: <b>{{taskData.numOfQuestions}}</b></div>
        <span>Attempts: <b>{{taskData.attempts ? taskData.attempts : 'Unlimited'}}</b></span>
        <span class="grey--text text--lighten-3">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
        <span>Time: <b>{{taskData.timeAllowed ? taskData.timeAllowed + ' mins' : 'Unlimited'}}</b></span>
      </div>
      <div v-if="test" class="my-3 text-xs-center">
        <v-btn @click.native="startTest" class="title" color="success" :disabled="!startable">Continue</v-btn>
        <div class="question-body secondary--text pt-1">
          <v-icon class="mb-2" color="secondary">alarm</v-icon>
          <b>{{taskData.timeAllowed ? timeLeftString : 'Unlimited Time'}}</b>
        </div>
      </div>
      <div v-else class="my-3 text-xs-center">
        <div v-if="taskData.attempts">
          <div v-if="testAttempts.length >= parseInt(taskData.attempts)" class="error--text"><b>No Attempts Left</b></div>
        </div>
        <v-btn @click.native="startTest" class="title" color="success" :disabled="!startable">Start</v-btn>
      </div>
      <div class="title mb-2 mt-4 text-xs-center"><b>Previous Attempts</b></div>
      <div v-if="testAttempts.length == 0" class="my-5 display-1 grey--text text--lighten-2">None</div>
      <v-layout v-else class="px-2 pb-3" row wrap justify-center>
        <template v-for="(attempt, i) in testAttempts">
          <v-flex :key="`attempt-${i}`" class="text-xs-left" xs1>{{testAttempts.length - i}}</v-flex>
          <v-flex :key="`attempttime-${i}`" class="text-xs-left" xs10 md8>{{(new Date(attempt.startTime)).toLocaleString()}}</v-flex>
          <v-flex :key="`attemptbody-${i}`" class="text-xs-right" xs1>
            <span
              v-if="now < attempt.endTime"
              class="grey--text"
            ><b>--%</b></span>
            <span
              v-else-if="attempt.grade >= passGrade"
              class="success--text"
            ><b>{{attempt.grade + '%'}}</b></span>
            <span
              v-else-if="attempt.grade >= almostPassGrade"
              class="accent--text"
            ><b>{{attempt.grade + '%'}}</b></span>
            <span
              v-else
              class="error--text"
            ><b>{{attempt.grade + '%'}}</b></span>
          </v-flex>
          <v-flex :key="`divider-${i}`" v-if="i != (testAttempts.length - 1)" xs12 md10><v-divider class="my-1"></v-divider></v-flex>
          <v-flex :key="`linebreak-${i}`" xs12></v-flex>
        </template>
      </v-layout>
    </v-flex>
  </v-layout>
  <v-layout v-else-if="status == 'IN PROGRESS' && test" row wrap justify-center>
    <v-flex class="pt-1 text-xs-center" xs8 sm5 md4>
      <div class="headline pa-2 secondary--text">
        <v-icon class="mb-2" color="secondary" medium>alarm</v-icon>
        <b>{{taskData.timeAllowed ? timeLeftString : 'Unlimited Time'}}</b>
      </div>
    </v-flex>
    <v-flex xs12>
      <div class="question-body pa-2"><b>Question #{{test.currentQuestion + 1}}</b></div>
      <div @keydown="disableCopy($event)" tabindex="0" class="question-body pa-2 mb-2">{{questionData.body}}</div>
    </v-flex>
    <v-flex class="text-xs-center" xs12>
      <v-card
        @click.native="!test.graded ? chooseAnswer(i) : ''"
        @mouseover="!test.graded ? answer.hover = true : ''"
        @mouseout="!test.graded ? answer.hover = false : ''"
        style="cursor: pointer"
        v-for="(answer, i) in questionData.answers"
        :key="i"
        class="pa-2 mb-2"
        :color="(test.graded && answer.correct && answer.selected) ? 'success' : (test.graded && !answer.correct && answer.selected) ? 'error' : answer.selected ? 'grey lighten-2 elevation-1' : answer.hover ? 'grey lighten-4 elevation-0' : 'elevation-0'">
        <v-layout row wrap>
          <v-flex class="pr-2" xs1>
            <div class="question-body pt-1">{{answer.letter}}.</div>
          </v-flex>
          <v-flex class="pl-2 text-xs-left" xs11>
            <div @keydown="disableCopy($event)" tabindex="1" class="question-body pt-1">{{answer.text}}</div>
          </v-flex>
        </v-layout>
      </v-card>
    </v-flex>
    <v-flex v-if="test.graded" class="pb-3 text-xs-center" xs12>
      <div class="headline pb-3">
        <span v-if="test.correct" class="success--text caps"><b>Correct</b></span>
        <span v-else class="error--text caps"><b>Wrong</b></span>
      </div>
      <div class="question-body text-xs-left">
        <span><b>Rationale</b></span><br>
        <span>{{questionData.rationale}}</span>
      </div>
    </v-flex>
    <v-flex class="pb-3 text-xs-center" xs12>
      <v-btn v-if="!test.graded" @click.native="gradeTest" class="title" color="primary" large><b>Check</b></v-btn>
      <v-btn v-else @click.native="nextQuestion" class="title" color="success" large><b>Next</b></v-btn>
    </v-flex>
  </v-layout>
  <v-layout v-else-if="status == 'FINISHED'" row wrap justify-center>
    <v-flex class="pt-4 pb-5 mb-3 text-xs-center" xs12>
      <div class="headline">
        <span>Attempt {{testAttempts.length}}</span>
      </div>
      <div class="score-text">
        <span
          v-if="testAttempts[0].grade >= passGrade"
          class="success--text"
        ><b>{{testAttempts[0].grade + '%'}}</b></span>
        <span
          v-else-if="testAttempts[0].grade >= almostPassGrade"
          class="accent--text"
        ><b>{{testAttempts[0].grade + '%'}}</b></span>
        <span
          v-else
          class="error--text"
        ><b>{{testAttempts[0].grade + '%'}}</b></span>
      </div>
      <v-btn @click.native="leave" class="title mt-4" color="success" large><b>Done</b></v-btn>
    </v-flex>
  </v-layout>
</div>
</template>

<script>
export default {
  data: () => ({
    loading: false,
    startable: true,
    status: 'START',
    possibleQuestions: [],
    questionData: {},
    currentProgress: null,
    taskData: null,
    timer: null
  }),
  beforeMount () {
    this.load()
  },
  computed: {
    now () {
      return this.$store.getters.now
    },
    test () {
      for (let t of this.$store.getters.taskProgress) {
        if (t.taskKey === this.currentProgress.task && t.progressKey === this.currentProgress.progress && t.endTime) {
          if (t.endTime > this.now) return t
        }
      }
      if (this.status === 'IN PROGRESS') {
        this.status = 'FINISHED'
      }
      return undefined
    },
    testAttempts () {
      return this.$store.getters.testAttempts(this.currentProgress.task, this.currentProgress.progress).reverse()
    },
    timeLeftString () {
      if (this.test) {
        if (this.test.endTime > this.now) {
          const time = new Date(this.test.endTime - this.now)
          let string = ''
          let hrs = time.getUTCHours()
          let mins = time.getUTCMinutes()
          let secs = time.getUTCSeconds()
          let hrString = hrs > 9 ? ('' + hrs) : ('0' + hrs)
          let minString = mins > 9 ? ('' + mins) : ('0' + mins)
          let secString = secs > 9 ? ('' + secs) : ('0' + secs)
          string += hrs > 0 ? (hrString + ':') : ''
          string += minString + ':' + secString
          return string
        } else return 'Ended'
      } else return ''
    },
    passGrade () {
      return this.taskData ? this.taskData.passGrade || 65 : 65
    },
    almostPassGrade () {
      return Math.floor(this.passGrade - (this.passGrade * 0.2))
    }
  },
  methods: {
    load () {
      this.loading = true
      this.currentProgress = this.$store.getters.currentProgress
      this.taskData = this.$store.getters.taskByKey(this.currentProgress.task)
      if (this.taskData.type === 'Linking') this.taskData = this.$store.getters.taskByKey(this.taskData.linkedTaskKey)
      this.getPossibleQuestions()
      this.loading = false
    },
    getPossibleQuestions () {
      const q = this.$store.getters.questions
      let possibleEasy = []
      let possibleHard = []
      let tagsFound = false
      const appMsg = {
        type: 'error',
        msg: ''
      }
      for (let question of q) {
        tagsFound = true
        for (let tag of this.taskData.tags) {
          if (!question.tags.includes(tag)) {
            tagsFound = false
            break
          }
        }
        if (tagsFound) {
          if (question.tags.includes('Easy')) possibleEasy.push(question.key)
          else possibleHard.push(question.key)
        }
      }
      let numEasy = 0
      if (this.taskData.percentEasy) {
        numEasy = Math.round(parseInt(this.taskData.numOfQuestions) * (parseInt(this.taskData.percentEasy) / 100))
      }
      if (possibleEasy.length + possibleHard.length < parseInt(this.taskData.numOfQuestions)) {
        appMsg.msg = 'Questions error, contact instructor'
        this.$store.dispatch('showAppMsg', appMsg)
        this.startable = false
        return
      }
      if (possibleEasy.length < numEasy || possibleHard.length < (parseInt(this.taskData.numOfQuestions) - numEasy)) {
        appMsg.msg = 'Questions error, contact instructor'
        this.$store.dispatch('showAppMsg', appMsg)
        this.startable = false
        return
      }
      if (this.taskData.attempts) {
        let finishedAttempts = 0
        for (const ta of this.testAttempts) if (this.now >= ta.endTime) finishedAttempts += 1
        if (finishedAttempts >= parseInt(this.taskData.attempts)) this.startable = false
      }
      this.possibleQuestions = possibleEasy.concat(possibleHard)
    },
    startTest () {
      this.loading = true
      if (this.test) {
        this.loadQuestion()
        this.startTimer()
        this.status = 'IN PROGRESS'
        this.loading = false
      } else {
        const newTaskProgress = {
          taskKey: this.currentProgress.task,
          progressKey: this.currentProgress.progress,
          studentId: this.$store.getters.user.uid,
          startTime: this.now,
          endTime: this.now + (this.$store.getters.time.minute * parseInt(this.taskData.timeAllowed)),
          questions: this.getQuestions(),
          currentQuestion: 0,
          graded: false,
          correct: false,
          choice: [],
          score: 0,
          grade: 0
        }
        this.$store.dispatch('createTaskProgress', newTaskProgress).then(() => {
          this.loadQuestion()
          this.startTimer()
          this.status = 'IN PROGRESS'
          this.loading = false
        }).catch(err => {
          const appMsg = {
            type: 'error',
            msg: 'Fatal error while creating test. Please refresh page.'
          }
          console.log(err)
          this.$store.dispatch('showAppMsg', appMsg)
        })
      }
    },
    getQuestions () {
      let easy = 0
      let normal = 0
      let questions = []
      let q = null
      let random = null
      let numEasy = 0
      if (this.taskData.percentEasy) {
        numEasy = Math.round(parseInt(this.taskData.numOfQuestions) * (parseInt(this.taskData.percentEasy) / 100))
      }
      while (questions.length < parseInt(this.taskData.numOfQuestions)) {
        random = this.$store.getters.rng(0, this.possibleQuestions.length - 1)
        q = this.$store.getters.questionByKey(this.possibleQuestions[random])
        if (!questions.includes(this.possibleQuestions[random])) {
          if (this.taskData.percentEasy) {
            if (q.tags.includes('Easy') && numEasy > easy) {
              questions.push(q.key)
              easy++
            } else if ((parseInt(this.taskData.numOfQuestions) - numEasy) > normal) {
              questions.push(q.key)
              normal++
            }
          } else {
            questions.push(q.key)
          }
        }
      }
      return questions
    },
    loadQuestion () {
      this.loading = true
      const q = this.$store.getters.questionByKey(this.test.questions[this.test.currentQuestion])
      let qData = {
        ...q,
        questionNum: this.test.currentQuestion,
        correct: false,
        tags: q.tags ? [...q.tags] : [],
        area: q.area ? [...q.area] : [],
        answers: []
      }
      for (let answer of q.answers) {
        qData.answers.push({
          ...answer,
          hover: false,
          selected: false
        })
      }
      this.questionData = qData
      if (this.test.graded && this.test.choice) {
        for (let i of this.test.choice) {
          this.chooseAnswer(i)
        }
      }
      this.loading = false
    },
    gradeTest () {
      this.loading = true
      let newTaskProgress = {
        ...this.test,
        questions: [...this.test.questions],
        choice: [],
        correct: true,
        graded: true
      }
      for (let i = 0; i < this.questionData.answers.length; i++) {
        if (this.questionData.answers[i].selected) newTaskProgress.choice.push(i)
        if (this.questionData.answers[i].correct !== this.questionData.answers[i].selected) newTaskProgress.correct = false
      }
      if (newTaskProgress.correct) newTaskProgress.score += 1
      newTaskProgress.grade = Math.round((newTaskProgress.score / newTaskProgress.questions.length) * 100)
      this.$store.dispatch('updateTaskProgress', newTaskProgress).then(() => {
        this.loading = false
      }).catch(err => {
        console.log(err.message)
        const appMsg = {
          type: 'error',
          msg: 'Fatal error while grading question. Please refresh page.'
        }
        this.$store.dispatch('showAppMsg', appMsg)
      })
    },
    nextQuestion () {
      if (this.test.currentQuestion < parseInt(this.taskData.numOfQuestions) - 1) {
        this.loading = true
        let newTaskProgress = {
          ...this.test,
          questions: [...this.test.questions],
          currentQuestion: this.test.currentQuestion + 1,
          choice: [],
          graded: false
        }
        this.$store.dispatch('updateTaskProgress', newTaskProgress).then(() => {
          this.$store.dispatch('getTime').then(now => {
            this.$store.commit('setNow', now)
            this.loadQuestion()
            this.loading = false
          }).catch(err => {
            const appMsg = {
              type: 'error',
              msg: 'Fatal error while changing questions. Please refresh the page.'
            }
            console.log(err)
            this.$store.dispatch('showAppMsg', appMsg)
          })
        }).catch(err => {
          const appMsg = {
            type: 'error',
            msg: 'Fatal error while changing questions. Please refresh the page.'
          }
          console.log(err)
          this.$store.dispatch('showAppMsg', appMsg)
        })
      } else this.endTest()
    },
    endTest () {
      this.loading = true
      let newTaskProgress = {
        ...this.test,
        questions: [...this.test.questions],
        endTime: this.now
      }
      this.$store.dispatch('updateTaskProgress', newTaskProgress).then(() => {
        this.stopTimer()
        this.status = 'FINISHED'
        this.loading = false
      }).catch(err => {
        console.log(err.message)
        const appMsg = {
          type: 'error',
          msg: 'Fatal error while submitting test. Please refresh the page.'
        }
        this.$store.dispatch('showAppMsg', appMsg)
      })
    },
    chooseAnswer (index) {
      if (this.questionData.type === 'Select All That Apply') {
        for (let i = 0; i < this.questionData.answers.length; i++) {
          if (i === index) this.questionData.answers[i].selected = !this.questionData.answers[i].selected
        }
      } else {
        for (let i = 0; i < this.questionData.answers.length; i++) {
          if (i === index) this.questionData.answers[i].selected = true
          else this.questionData.answers[i].selected = false
        }
      }
    },
    startTimer () {
      this.timer = setInterval(() => {
        if (this.test && this.status !== 'START') {
          if (this.now > this.test.endTime) {
            this.loading = true
            this.status = 'FINISHED'
            this.loading = false
          } else if (this.questionData.questionNum !== this.test.currentQuestion) this.loadQuestion()
        }
      }, 1000)
    },
    stopTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    getGrade (score, numOfQuestions) {
      return Math.round((score / numOfQuestions) * 100)
    },
    leave () {
      let currentProgress = this.$store.getters.currentProgress
      this.$store.dispatch('setCurrentProgress', {
        progress: currentProgress.progress,
        section: currentProgress.section,
        task: null
      })
      this.$router.push('/my-courses/course-view/my-tasks')
    },
    disableCopy (event) {
      if (event.key === 'c' && event.ctrlKey) event.preventDefault()
    }
  }
}
</script>

<style>
.question-body {
  font-size: 20px;
  line-height: 150%;
}
.question-body:focus {
  outline: none;
}
.score-text {
  font-size: 170px;
  line-height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/myCourses/PracticeTask.vue